import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useForm } from 'react-hook-form'
import Modal from 'react-bootstrap-modal'
import DateInputWrapper from './date-input-wrapper'
import SelectInputWrapper from './select-input-wrapper'
import TextInputWrapper from './text-input-wrapper'
import NumberInputWrapper from './number-input-wrapper'
import PriceInputWrapper from './price-input-wrapper'
import CheckboxWrapper from './checkbox-wrapper'
import NotifyPlayersCheckbox from './notify-players-checkbox'
import FeaturedPhotoWrapper from './featured-photo-wrapper'
import TextAreaWrapper from './text-area-wrapper'
import AdditionalGames from './additional-games'
import Recurring from './recurring'
import Fields from './fields'
import FlashAlert from '../flash-alert'
import GoogleConflictWarning from '../google-conflict-warning'
import NotifyModal from './notify-modal'
import { ajaxGet, postForm, patchForm } from '../../ajax'
import { handleGameFormSubmitResponse, handleGameFormSubmitError } from '../../utils/handle-game-form-submit-response'
import moment from 'moment'

function GameForm({type, id, recurringId, params, onClose, onUpdate}) {
  const [payload, setPayload] = useState()
  const [initialValues, setInitialValues] = useState()
  const [showTo, setShowTo] = useState(false)
  const [showTerms, setShowTerms] = useState(false)
  const [loading, setLoading] = useState(false)
  const [showRecurring, setShowRecurring] = useState(false)
  const [showNotifyModal, setShowNotifyModal] = useState(false)
  const [baseField, setBaseField] = useState()
  const [subGames, setSubGames] = useState([])
  const [endDate, setEndDate] = useState()
  const [alert, setAlert] = useState()
  const [googleConflict, setGoogleConflict] = useState(false)
  const [googleCalendarId, setGoogleCalendarId] = useState()

  const isEdit = (type == 'edit')
  const isRecurring = type == 'recurring'
  const isDuplicate = type == 'duplicate'

  const { register, unregister, setError, clearErrors, getValues, setValue, handleSubmit, formState: { errors } } = useForm()

  const fieldsToNotify = ['name', 'game_size', 'min_player_count', 'max_player_count', 'cost',
    'price_per_player', 'skill_level', 'males_only', 'females_only', 'teams_count', 'about_event']

  useEffect(() => {
    const defaultParams = {}
    if (params) {
      if (params.field) defaultParams['game[field_ids][]'] = params.field
      if (params.date) defaultParams['game[date]'] = params.date
      if (params.default_time) defaultParams['game[default_time]'] = params.default_time  
    }
    loadData(defaultParams)
  }, [])

  const formUrl = () => {
    switch (type) {
      case 'edit':
      case 'recurring':
        return `/admins/games/${id}/edit.json?`
      case 'duplicate':
        return `/admins/games/${id}/duplicate.json?`
      default:
        return '/admins/games/new.json?'
    }
  }

  const submitUrl = () => {
    if (isEdit) return `/admins/games/${id}.json?`
    if (isRecurring) return `/admins/recurring_reservations/${recurringId}.json?`
    
    return '/admins/games.json'
  }

  const loadData = (params) => {
    if (isRecurring) params['recurring_id'] = recurringId
    setLoading(true)

    ajaxGet(formUrl() + new URLSearchParams(params))
    .then(response => { if (response.ok) return response.json() })
    .then((json) => {
      setPayload(json)
      initForm(json.form)
      setEndDate(json.date)
      if (_.isEmpty(params)) setInitialValues(json.form)
      if (!_.isEmpty(params)) setTimes(json)
      if (_.isEmpty(params) && isDuplicate) {
        if (json.reservation_image_id) {
          setValue('reservation_image_id', json.reservation_image_id)
        } else if (json.featured_photo_url) {
          const url = typeof json.featured_photo_url === 'string'
            ? json.featured_photo_url
            : json.featured_photo_url.url
          if (url) setValue('featured_photo_url', url)
        }
      }
      setLoading(false)
    })
  }
  
  const initForm = (data) => {
    data.duration && setShowTo(data.duration == 'custom')
    setShowTerms(data.partner_id)
    for (const [key, value] of Object.entries(data)) { setValue(key, value) }
    data.field_id && setBaseField(data.field_id)
    setGoogleCalendarId(data.calendar_id)
  }
  
  const prepareFormData = (data) => {
    let formData = new FormData()
    for (const key in data) {
      if (data[key] == undefined) continue
      if (key == 'notify_flag' || key == 'conflict_flag') continue
      if (key == 'times')
        data[key].forEach(v => v && formData.append('game[times][]', v))
      else
        formData.append(`game[${key}]`, data[key]) 
    }
    if (data.interval) formData.append('game[end_date]', endDate)
    if (isRecurring) formData.append('reservation_id', id)
    if (data.duration !== 'custom') formData.delete('game[duration_end_time]')
    formData.append('game[field_ids][]', baseField)
    subGames.forEach(game => formData.append('game[field_ids][]', game.field))
    return formData
  }

  const wasGameChaged = (data) => {
    if (initialValues.times[0] != data.times[0]) return true
    return fieldsToNotify.some(key => data[key] != initialValues[key])
  }

  const toShowNotifyModal = (data) => {
    if (!isEdit || data.send_update_to_player || !payload.with_players || !wasGameChaged(data)) return false

    return true
  }

  const onNotifyComplete = (toNotify) => {
    setShowNotifyModal(false)
    setValue('send_update_to_player', toNotify)
    setValue('notify_flag', true)

    handleSubmit(onSubmit)()
  }

  const onConflictProcessed = () => {
    setGoogleConflict(false)
    setValue('conflict_flag', true)

    handleSubmit(onSubmit)()
  }

  const checkGoogleConflicts = async (data) => {
    setLoading(true)

    const response = await ajaxGet('/admins/google_calendars/events?' + new URLSearchParams({
      start: data.times[0],
      end: moment(data.times[0]).add(data.duration, 'hours').format(),
      location_id: data.location_id,
      new_game: true
    }))
    const json = await response.json() 
    const events = json.events.filter((e) => e.resourceIds.includes(parseInt(baseField)))
    if (events.length) return events[0].title

    setLoading(false)
  }

  const onSubmit = async (data) => {
    if (!validateEndDate()) return
    if (!data.conflict_flag && !isRecurring && googleCalendarId) {
      const conflictGame = await checkGoogleConflicts(data)
      if (conflictGame) return setGoogleConflict({ gameName: conflictGame })
    }
    if (!data.notify_flag && toShowNotifyModal(data)) return setShowNotifyModal(true)
  
    processSubmit(data)
  }

  const processSubmit = (data) => {    
    setLoading(true)
  
    const formData = prepareFormData(data)
    const formMethod = (isEdit || isRecurring) ? patchForm : postForm

    formMethod(submitUrl(), formData)
    .then(response => {
      return response.json().then((json) => ({ response, json })).catch(() => ({ response, json: null }))
    })
    .then(({ response, json }) => {
      handleGameFormSubmitResponse(response, json, {
        setLoading,
        setAlert,
        onUpdate,
        onClose
      })
    })
    .catch((err) => {
      handleGameFormSubmitError(err, { setLoading, setAlert })
    })
  }

  const setTimes = (json) => {
    const timeSelects = document.querySelectorAll('select.time-select')
    timeSelects.forEach(function (element) {
      const text = element.options[element.selectedIndex].text
      const timeIndex = json.hours.findIndex((e) => e[1] == text)
      setValue(element.name, timeIndex == -1 ? '' : json.hours[timeIndex][0])
    })
  }

  const validateEndDate = () => {
    if (!showRecurring) return true

    let error = {}
    const interval = moment(new Date(endDate)).diff(new Date(baseDate), 'months', true)
    if (interval <= 0) error = { type: 'manual', message: 'Invalid interval'}
    if (interval > 12) error = { type: 'manual', message: 'Interval limit is 12 months'}
    if (!_.isEmpty(error)) {
      setError('end_date', error)
      return false
    }
    return true
  }

  const changeCompany = (companyId) => {
    const formValues = _.pick(getValues(), 'price_per_player', 'max_player_count', 'partner_id', 'term_id')
    const formParams = {
      'game[company_id]': companyId, 
      'game[date]': payload.date,
      'game[price_per_player]': formValues.price_per_player,
      'game[max_player_count]': formValues.max_player_count,
    }
    if (formValues.partner_id != '') {
      formParams['game[partner_id]'] = formValues.partner_id
      formParams['game[term_id]'] = formValues.term_id
    }
    loadData(formParams)
    setSubGames([])
  }

  const changeLocation = (locationId) => {
    const formValues = _.pick(getValues(), 'company_id', 'price_per_player', 'max_player_count', 
      'partner_id', 'term_id')
    const formParams = {
      'game[company_id]': formValues.company_id,
      'game[location_id]': locationId,
      'game[date]': payload.date,
      'game[price_per_player]': formValues.price_per_player,
      'game[max_player_count]': formValues.max_player_count,
    }
    if (formValues.partner_id != '') {
      formParams['game[partner_id]'] = formValues.partner_id
      formParams['game[term_id]'] = formValues.term_id
    }
    loadData(formParams)
    setSubGames([])
  }

  const changeGameOptions = (params, recost = true) => {
    const formValues = _.pick(getValues(), 'company_id', 'location_id', 'partner_id', 'term_id',
      'price_per_player', 'max_player_count', 'facility_cost', 'cost_type', 'todo_list')
    if (!formValues.location_id) return

    const formParams = {
      'game[company_id]': formValues.company_id,
      'game[location_id]': formValues.location_id,
      'game[price_per_player]': formValues.price_per_player,
      'game[max_player_count]': formValues.max_player_count,
      'game[date]': payload.date,
      'game[times][]': getValues('times')[0],
      'game[field_ids][]': baseField,
      'game[todo_list]': formValues.todo_list,
    }
    if (formValues.partner_id != '') {
      formParams['game[partner_id]'] = formValues.partner_id
      formParams['game[term_id]'] = formValues.term_id
    }
    if (!recost) {
      formParams['game[facility_cost]'] = formValues.facility_cost
      formParams['game[cost_type]'] = formValues.cost_type
    }
    loadData({...formParams, ...params})
  }

  const fileSelected = (file) => {
    const fr = new FileReader()
    fr.onloadend = function() { setValue('featured_photo_url', fr.result) }
    fr.readAsDataURL(file)
  }

  const addSubGame = () => {
    setSubGames([...subGames, {field: baseField, date: payload.date, hours: payload.hours}])
  }

  const removeSubGame = (index) => {
    const newSubGames = [...subGames.slice(0, index), ...subGames.slice(index + 1, subGames.length)]
    setSubGames(newSubGames)
  }

  const changeSubGame = (index, params) => {
    const newSubGames = [ 
      ...subGames.slice(0, index), 
      {...subGames[index], ...params },
      ...subGames.slice(index + 1, subGames.length) ]
    setSubGames(newSubGames)
  }

  const changeEndDate = (date) => {
    setEndDate(date)
    clearErrors('end_date')
  }

  const greaterThanFrom = (value) => {
    const from = getValues('times')[0]
    if (from >= value) return 'Must be > From'
    return true
  }

  const requiredMsg = 'Is required'
  
  if (!payload) return null

  const baseDate = payload.date
  const currencyName = payload.currency == 'usd' ? '' : ` (${payload.currency.toUpperCase()})`

  const checkKeyDown = (e) => {
    if (e.key === 'Enter' && e.target.type != 'textarea') {
      e.preventDefault()
      setValue(e.target.name, e.target.value)
      handleSubmit(onSubmit)()
    }
  }

  const setGenderRestriction = (key, value) => {
    setValue(key, value)

    if (!value) return

    setValue((key == 'males_only') ? 'females_only' : 'males_only', false)
  }

  return <Modal dialogClassName='open-game-form-modal' backdrop='static' container={document.body} show={true} onHide={onClose}>
    <Modal.Header closeButton>
      <Modal.Title>{`${(isEdit || isRecurring) ? 'Edit' : 'Create'} an Open Game`}</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <div className={'game-form' + (loading ? ' isLoading' : '')}>
        { loading && <div className='animated-spinner'></div> }
        { alert && <FlashAlert text={alert.text} type={alert.type} html={alert.html} constant centred onHide={() => setAlert(null)} />}
        <form className='form-horizontal' onSubmit={handleSubmit(onSubmit)} onKeyDown={(e) => checkKeyDown(e)}>
          <div className='row'>
            <div className='col-sm-8'>
              <SelectInputWrapper 
                {...register('company_id')} 
                label='Clients' 
                options={payload.companies}
                readOnly={isEdit || isRecurring}
                onChange={(e) => changeCompany(e.target.value)}
              />
            </div>
            <div className='col-sm-4'>
              <SelectInputWrapper 
                {...register('partner_id')} 
                label='Organizers' 
                options={payload.partners}
                onChange={e => {
                  changeGameOptions({'game[partner_id]': e.target.value, 'game[term_id]': ''}, false)
                  setShowTerms(e.target.value != '')
                  setValue('partner_id', e.target.value)
                }}
              />
            </div>
          </div>
          { showTerms && 
            <div className='row'>
              <div className='col-sm-12'>
                <SelectInputWrapper 
                  {...register('term_id')} 
                  label='Terms and Conditions' 
                  options={payload.terms}
                  onChange={e => setValue('term_id', e.target.value)}
                />
              </div>
            </div>
          }

          <div className='row'>
            <div className='col-sm-6'>
              <TextInputWrapper
                {...register('name')}
                label='Name'
                placeholder='Event Name'
                onChange={e => setValue('name', e.target.value)}
              />
            </div>
            <div className='col-sm-2'>
              <SelectInputWrapper
                {...register('game_size')}
                label='Game Size'
                options={payload.sizes}
                onChange={e => setValue('game_size', e.target.value)}
              />
            </div>
            <div className='col-sm-2'>
              <NumberInputWrapper 
                {...register('min_player_count', { 
                  required: requiredMsg, 
                  min: { value: 1, message: 'Must be >= 1' } 
                }) } 
                label='Min Players'
                error={errors.min_player_count}
                onChange={e => setValue('min_player_count', e.target.value, { shouldValidate: true })}
              />
            </div>
            <div className='col-sm-2'>
              <NumberInputWrapper 
                {...register('max_player_count', { 
                  required: requiredMsg, 
                  min: { value: 2, message: 'Must be >= 2' } 
                })} 
                label='Max Players' 
                error={errors.max_player_count}
                onChange={(e) => {
                  changeGameOptions({'game[max_player_count]': e.target.value}, false)
                  setValue('max_player_count', e.target.value, { shouldValidate: true })
                }}
              />
            </div>
          </div>

          <div className='row date-time-main'>
            <div className='col-sm-3'>
              <DateInputWrapper
                label='Date'
                date={moment(new Date(baseDate))}
                disabled={isRecurring}
                onChange={date => changeGameOptions({'game[date]': (date || moment()).format('MM/DD/YYYY')})}
              />
            </div>
            <div className='col-sm-2'>
              <SelectInputWrapper 
                {...register('duration')}
                label='Duration'
                options={payload.durations}
                onChange={(e) => {
                  setShowTo(e.target.value == 'custom')
                  setValue('duration', e.target.value, { shouldValidate: true })
                }}
              />
            </div>
            <div className='col-sm-2'>
              <SelectInputWrapper
                {...register('times[0]', { required: requiredMsg })}
                label='From'
                selectClassName='time-select'
                options={payload.hours}
                error={errors.times && errors.times[0]}
                onChange={(e) => {
                  changeGameOptions({'game[times][]': e.target.value})
                  setValue('times[0]', e.target.value, { shouldValidate: true })
                }}
              />
            </div>
            { showTo && 
              <div className='col-sm-2'>
                <SelectInputWrapper
                  {...register('duration_end_time', { required: requiredMsg, validate: greaterThanFrom })} 
                  label='To'
                  selectClassName='time-select'
                  options={payload.hours}
                  error={errors.duration_end_time}
                  onChange={(e) => setValue('duration_end_time', e.target.value, { shouldValidate: true })}
                />
              </div>
            }
            <div className='col-sm-3'>
              <span className='location-timezone'>{payload.timezone}</span>
            </div>
          </div>

          <div className='row'>
            { payload.locations && 
              <div className='col-sm-2'>
                <SelectInputWrapper 
                  {...register('location_id')} 
                  label='Location' 
                  options={payload.locations}
                  readOnly={isEdit || isRecurring}
                  onChange={e => changeLocation(e.target.value)}
                />
              </div>
            }
            <div className='col-sm-2'>
              <PriceInputWrapper 
                {...register('cost', { required: requiredMsg}) } 
                label={`Cost${currencyName}`}
                error={errors.cost}
                onChange={e => setValue('cost', e.target.value, { shouldValidate: true })}
              />
            </div>
            <div className='col-sm-2'>
              <SelectInputWrapper
                {...register('cost_type')}
                label='Cost Type'
                options={payload.cost_types}
                onChange={(e) => setValue('cost_type', e.target.value, { shouldValidate: true })}
              />
            </div>
            <div className='col-sm-2'>
              <PriceInputWrapper 
                {...register('facility_cost', { required: requiredMsg, min: { value: 0, message: 'Must be >= 0' } }) } 
                label={`Facility Cost${currencyName}`}
                error={errors.facility_cost}
                onChange={(e) => setValue('facility_cost', e.target.value, { shouldValidate: true })}
              />
            </div>
            <div className='col-sm-2'>
              <PriceInputWrapper 
                {...register('price_per_player', { required: requiredMsg}) } 
                label={`Price/Player${currencyName}`}
                error={errors.price_per_player}
                onChange={(e) => {
                  changeGameOptions({'game[price_per_player]': e.target.value}, false)
                  setValue('price_per_player', e.target.value, { shouldValidate: true })
                }}
              />
            </div>
            <div className='col-sm-2'>
              <SelectInputWrapper 
                {...register('skill_level')}
                label='Skill Level' 
                options={payload.skill_levels}
                onChange={e => setValue('skill_level', e.target.value, { shouldValidate: true })}
              />
            </div>
          </div>

          <div className='row'>
            <div className='col-sm-2'>
              <CheckboxWrapper
                {...register('include_fakes')}
                label='Include Fakes'
                onChange={(e) => setValue('include_fakes', e.target.checked)}
              />
            </div>
            <div className='col-sm-2'>
              <CheckboxWrapper
                {...register('males_only')}
                label='Males Only'
                onChange={e => setGenderRestriction('males_only', e.target.checked)}
              />
            </div>
            <div className='col-sm-2'>
              <CheckboxWrapper
                {...register('females_only')}
                label='Females Only'
                onChange={e => setGenderRestriction('females_only', e.target.checked)}
              />
            </div>
            <div className='col-sm-2'>
              <SelectInputWrapper
                {...register('extra_notes')}
                label='Extra Note'
                options={payload.extra_notes}
                onChange={(e) => setValue('extra_notes', e.target.value, { shouldValidate: true })}
              />
            </div>
            <div className='col-sm-2'>
              <NumberInputWrapper 
                {...register('teams_count', { 
                  required: requiredMsg, 
                  min: { value: 2, message: 'Must be >= 2' } 
                }) } 
                label='Teams'
                error={errors.teams_count}
                onChange={e => setValue('teams_count', e.target.value, { shouldValidate: true })}
              />
            </div>
          </div>
          <div className='row'>
            <div className='col-sm-2'>
              <CheckboxWrapper
                {...register('nonpublic')}
                label='Private'
                onChange={(e) => setValue('nonpublic', e.target.checked)}
              />
            </div>
            <div className='col-sm-2'>
              <CheckboxWrapper
                {...register('auto_confirm')}
                label='Auto Confirm'
                onChange={(e) => setValue('auto_confirm', e.target.checked)}
              />
            </div>
            <div className='col-sm-2'>
              <CheckboxWrapper
                {...register('auto_cancel')}
                label='Auto Cancel'
                onChange={(e) => setValue('auto_cancel', e.target.checked)}
              />
            </div>
            <div className='col-sm-2'>
              <CheckboxWrapper
                {...register('requires_captain')}
                label='Requires Captain'
                onChange={(e) => setValue('requires_captain', e.target.checked)}
              />
            </div>
          </div>

          { payload.fields && baseField &&
            <Fields 
              selected={baseField}
              fields={payload.fields} 
              fieldIndex={0}
              onChangeField={(value) => setBaseField(value)}
            /> 
          }

          <FeaturedPhotoWrapper type={type} photoUrl={payload.featured_photo_url} onFileLoad={fileSelected} />
          <TextAreaWrapper
            {...register('todo_list')}
            label='To do'
            onChange={(e) => {
              setValue('todo_list', e.target.value)
            }}
          />
          <TextAreaWrapper
            {...register('instructions')}
            label='Instructions'
            onChange={(e) => setValue('instructions', e.target.value)}
          />
          <TextAreaWrapper
            {...register('about_event')}
            label='About this event'
            onChange={e => setValue('about_event', e.target.value)}
          />
          <TextAreaWrapper
            {...register('about_facility')}
            label='About the facility'
            onChange={(e) => setValue('about_facility', e.target.value)}
          />
          <TextAreaWrapper
            {...register('where_to_find')}
            label='Where to find us'
            onChange={(e) => setValue('where_to_find', e.target.value)}
          />
          { isEdit && 
            <NotifyPlayersCheckbox
              {...register('send_update_to_player')}
              label='Send game time update to players'
              onChange={(e) => setValue('send_update_to_player', e.target.checked)}
            />
          }
          { baseField && !isEdit && 
            <AdditionalGames
              unregister={unregister}
              register={register}
              errors={errors}
              setValue={setValue}
              getValues={getValues}
              locationId={getValues('location_id')}
              fields={payload.fields}
              subGames={subGames}
              onAdd={addSubGame}
              onRemove={removeSubGame}
              onChange={changeSubGame}
              onLoading={setLoading}
            />
          }

          { !isEdit && 
            <div className='row'>
              <div className='col-sm-12'>
                <button type='button' className='btn btn-link clickable recurrings' onClick={() => setShowRecurring(!showRecurring)}>
                  <i className='fa fa-lock'></i>{' '}
                  Recurring Reservations
                </button>
              </div>
            </div>
          }
          { showRecurring &&
            <Recurring 
              register={register}
              unregister={unregister}
              errors={errors}
              setValue={setValue}
              intervals={payload.intervals}
              baseDate={baseDate}
              endDate={endDate}
              onDateChange={changeEndDate}
            />
          }

          <Modal.Footer>
            { alert && <FlashAlert text={alert.text} type={alert.type} html={alert.html} constant onHide={() => setAlert(null)} />}
            <input 
              type='submit' 
              name='commit' 
              value={(isEdit || isRecurring) ? 'Update' : 'Create Event'}
              className='btn btn-primary' 
            />
          </Modal.Footer>
          { googleConflict &&
            <GoogleConflictWarning
              onClose={() => setGoogleConflict(false)}
              googleConflict={googleConflict}
              onConfirm={onConflictProcessed}
              type='schedule'
            />
          }
          { showNotifyModal && 
            <NotifyModal
              onYes={() => onNotifyComplete(true)}
              onNo={() => onNotifyComplete(false)}
              onClose={() => setShowNotifyModal(false)}
            />
          }
        </form>
      </div>
    </Modal.Body>
  </Modal>
}

GameForm.propTypes = {
  type: PropTypes.string,
  id: PropTypes.number,
  recurringId: PropTypes.number,
  params: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired
}

export default GameForm
